/**
 * THE POT BAR: the whole decision in one picture.
 *
 * How high the climb is, what grabbing right now is worth, what every rung ahead is worth, and
 * the two ranges the game's own hidden thresholds are drawn from — punished, knife-edge,
 * gambling. Everything on it is `./duel-pot.ts`' reading of the server's own percentage: there
 * is no clock here, no interpolation and no local climb, which is what makes it right across a
 * stall, a reconnect and the settling freeze at the reveal.
 *
 * ## IT IS ITS OWN COMPONENT BECAUSE IT HAS A SECOND MOUNT
 *
 * It was markup inside `DuelOverlay` and it is a component now, taking a `DuelPotReadout` and
 * nothing else — no `DuelView`, no connection, no wire. That is what lets an instructions page
 * mount the same bar against a percentage it made up, rather than a second drawing of the same
 * mechanic drifting away from the one players actually read. `StaminaBar`'s and `VoteCountdown`'s
 * arrangement: the readout is pure, the component is a picture of it, and the caller decides
 * where the number came from.
 *
 * ## WHY EVERY RUNG IS LABELLED, AND NOT ONLY THE NEXT ONE
 *
 * The bar used to mark the rungs and print what the *next* one was worth. A player deciding
 * under a ten-second freeze is choosing between rungs, so a ladder they can only read one step
 * of is a ladder they cannot choose on: the multipliers are already derived (`duelPotRungs`), and
 * printing them costs nothing but the row they sit in.
 *
 * ## THE BANDS ARE GRADIENTS, AND THE GRADIENT *IS* THE PROBABILITY
 *
 * The arm point is drawn uniformly across the penalty band, so the odds that a grab is early fall
 * linearly with the climb — 90% at 5%, 10% at 45%. A flat block would say those two are equally
 * dangerous. So the penalty band fades OUT toward its top and the bust band fades IN toward its
 * top, and the shape of the ink is the shape of the risk rather than a decoration.
 *
 * **Background gradients, never glows.** The installed shell strips `backdrop-filter` and every
 * `box-shadow` on a blurred element (`html[data-compositor='cheap']` in `app.css`), so a bloom
 * would exist on a desktop and be silently absent on the phone this is played on — the same
 * argument `.notice-card` is built under. A background is a paint on the element itself and
 * survives that profile untouched.
 *
 * **Battery saver settles movement and never takes a band, a label or a mark away**: fidelity,
 * never visibility (CLAUDE.md, "Battery on a phone"). `still` reaches the fill's transition and
 * the danger breath and nothing else in this file.
 *
 * ## THE RULER, AND WHY THE BUST FLOOR IS ONE MARK RATHER THAN THREE
 *
 * The rung multipliers are printed above the track, so the percentages go below it: two rows of
 * type sandwiching a bar, which is one more row than this card had and is the whole of what it
 * costs. Which numbers those are is `potScaleTicks` reading the width the track was actually
 * given — five on every surface this bar is really drawn on, three on one narrow enough that
 * five would be a wall of type — rather than five literals typed here, which would be right on a
 * desktop and wrong on a phone.
 *
 * **THE EXISTING DANGER MARK *BECAME* THE LINE; NOTHING WAS ADDED BESIDE IT.** There was already
 * a hairline at the bust floor, and the card asked for a line there: so that element is the line
 * — two pixels, centred on the point, opaque — and there is no second element saying the same
 * thing. The ruler prints no tick glyphs of its own for the same reason: at the floor it would
 * be a mark under a mark. What is left is three *different* sentences about one point — the band
 * gradient says how fast the risk grows, the line says where it starts, and the number under it
 * says what that point is called — and only one of them is a mark.
 *
 * **The line is painted white | rose | white, and that is a bug fix rather than a flourish.** It
 * was a `bg-destructive/70` hairline, and the fill turns `bg-destructive` the instant the climb
 * reaches the floor — so the mark disappeared under the fill at exactly the moment it started to
 * matter. One colour cannot cover a bare track, a primary fill and a destructive fill at once, so
 * it is three: the core carries the first two and the flanks carry the third. The flanks are also
 * what tell it from a rung tick, which is a plain bar in `primary-foreground` — and that colour is
 * *white* in the light theme, so a white line would have been a fourth identical mark of which
 * only three have a multiplier printed over them. `.chompdome-pot-bust-line` in `app.css`, a
 * background and never a glow.
 *
 * ## WHAT IS DRAWN IS THE DECLARED RANGE AND NEVER THE DRAWN POINT
 *
 * The bands come off `MiniGamePot.armMinPercent` / `.armMaxPercent` / `.bustMinPercent` /
 * `.bustMaxPercent`, which are shared constants every build carries and are identical for every
 * duel. Where *this* pot arms and where it bursts are drawn per duel from the room's seeded
 * stream, are on no frame and in neither codec, and shading either would hand over the answer.
 * See `./duel-pot.ts`.
 */

import { type RefObject, useLayoutEffect, useRef, useState } from 'react';
import type { DuelPotBand, DuelPotReadout } from './duel-pot';
import { formatPotMultiplier, potScaleTicks } from './duel-pot';

export interface DuelPotBarProps {
  pot: DuelPotReadout;
  /** True once the duel has revealed: the climb is the server's frozen settling value. */
  settled?: boolean;
  /** Battery saver: the movement goes, every number, band and mark stays. */
  still?: boolean;
}

export function DuelPotBar({ pot, settled = false, still = false }: DuelPotBarProps) {
  const danger = pot.inDanger && !settled && !still;
  const track = useRef<HTMLDivElement | null>(null);
  const scale = potScaleTicks(useTrackWidth(track));

  return (
    <div className="mt-3" data-testid="duel-pot">
      <div className="flex items-baseline justify-between gap-2">
        <span className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
          {settled ? 'Pot settled at' : 'Pot'}
        </span>
        <span className="flex items-baseline gap-2">
          <span
            data-testid="duel-pot-percent"
            className={`text-2xl font-black tabular-nums ${
              pot.inDanger ? 'text-destructive' : 'text-primary'
            }`}
          >
            {pot.percent}%
          </span>
          <span
            data-testid="duel-pot-multiplier"
            className="text-sm font-bold tabular-nums text-primary"
          >
            {formatPotMultiplier(pot.multiplier)}
          </span>
        </span>
      </div>

      {/*
       * THE LADDER, ABOVE THE TRACK: what each rung is worth, over the point it starts at.
       *
       * Centred on its own rung and clipped by nothing, so the outermost label may hang a few
       * pixels past the track — which is right: the mark is the truth and the label is a caption
       * for it. A reached rung is drawn in the pot's own colour and an unreached one is muted, so
       * "what have I already banked" and "what am I holding for" are told apart without reading.
       */}
      {pot.rungs.length === 0 ? null : (
        <div aria-hidden className="relative mt-2 h-3 text-[9px] font-bold tabular-nums">
          {pot.rungs.map((rung) => (
            <span
              key={rung.percent}
              data-testid="duel-pot-rung-label"
              data-rung={rung.percent}
              className={`absolute -translate-x-1/2 whitespace-nowrap leading-none ${
                rung.reached ? 'text-primary' : 'text-muted-foreground'
              }`}
              style={{ left: `${rung.percent}%` }}
            >
              {formatPotMultiplier(rung.multiplier)}
            </span>
          ))}
        </div>
      )}

      {/*
       * The track is a picture of the two numbers above it and carries no ARIA of its own: the
       * climb, the multiplier and the rungs are all visible text beside the label "Pot", so a
       * screen reader is already being told the thing, and a `meter` here would say it twice.
       *
       * The paint order inside it is explicit rather than left to the block/positioned rule,
       * because it is a decision: the bands are the *track*, the fill is where the climb has
       * reached, and the marks are on top of both. A band drawn over the fill would tint the one
       * thing on this bar that has to read as a single solid quantity.
       *
       * **THE CLIMB IS A THINNER BAR DOWN THE MIDDLE OF THE BANDS RATHER THAN A FULL-HEIGHT FILL,
       * AND THAT IS THIS BAR'S CENTRAL FIX.** The fill used to be `h-full`, so it painted over the
       * first `percent`% of the gradient — which meant the risk ramp was hidden exactly when a
       * player most needs it, since the interesting decisions all happen high on the climb. At the
       * top rung it covered four fifths of the thing it was drawn beside, and the instructions
       * example is frozen at that very rung (`instructionPotPercent`), so the page whose job is to
       * teach the bands was the one drawing almost none of them.
       *
       * Insetting it answers that without weakening the rule above: the bands are still under the
       * climb and are never drawn over it, so the climb still reads as a single solid quantity —
       * it is simply narrow enough to let the ramp run past it, top and bottom, for the full width
       * of the track. It costs no row of type, which is what the `duel-pot.ts` header's small-screen
       * argument demands; the track itself goes 12px -> 16px so the two exposed strips are 5px
       * rather than 3px, which is the whole price.
       */}
      <div
        ref={track}
        aria-hidden
        className="relative mt-1 h-4 overflow-hidden rounded-full bg-border"
      >
        {pot.penaltyBand === null ? null : (
          <BandShade
            band={pot.penaltyBand}
            testId="duel-pot-band-early"
            className="chompdome-pot-band-early"
          />
        )}
        <BandShade
          band={pot.bustBand}
          testId="duel-pot-band-bust"
          className="chompdome-pot-band-bust"
        />
        {/*
         * AMBER IN DANGER RATHER THAN `destructive`, AND THE REASON IS THE GROUND IT IS DRAWN ON.
         * Both bands are `--color-destructive` ramps, so a destructive climb bar past the bust
         * floor is rose on rose — the one place on this track where the climb and the risk behind
         * it are the same colour, and the one place a player is reading both at once. Amber is
         * already this HUD's warning tone (`ActionButtons`' burning-window chip) and it separates
         * from the rose ramp at every alpha the bands reach.
         *
         * The big percentage above the track keeps `text-destructive`, deliberately: it is drawn on
         * the card's own ground rather than on the bands, so it has no legibility constraint to
         * answer and rose is what danger reads as everywhere else in this client.
         */}
        <div
          data-testid="duel-pot-fill"
          className={`absolute left-0 top-1/2 z-10 h-1.5 -translate-y-1/2 rounded-full ${
            pot.inDanger ? 'bg-amber-400' : 'bg-primary'
          } ${still ? '' : 'chompdome-pot-fill'} ${danger ? 'chompdome-pot-danger' : ''}`}
          style={{ width: `${pot.percent}%` }}
        />
        {/*
         * THE BUST FLOOR, AS A LINE. From here on any tick may be the last one — and it is the
         * *declared* floor every build carries, never the point this duel rolled, which is on no
         * frame and nowhere this component could reach it (see `./duel-pot.ts`).
         *
         * It is the mark that was already here, promoted rather than joined by a second one: four
         * pixels, centred on its own point, painted white | rose | white so that it reads over the
         * bare track, over the primary fill and over the destructive fill the bar turns the moment
         * the climb passes this point — which is what the hairline it replaces could not do, since
         * it was `bg-destructive/70` and vanished under a fill of its own colour. The flanks are
         * also what tell it from a rung tick, whose `primary-foreground` is white in the light
         * theme. `.chompdome-pot-bust-line` in `app.css`; a class rather than an inline paint, so
         * the `left` attribute stays the plain percentage the overlay's own suite reads.
         */}
        <span
          aria-hidden
          data-testid="duel-pot-danger-mark"
          className="chompdome-pot-bust-line absolute inset-y-0 z-20 w-1 -translate-x-1/2"
          style={{ left: `${pot.dangerFromPercent}%` }}
        />
        {pot.rungs.map((rung) => (
          <span
            key={rung.percent}
            aria-hidden
            data-testid="duel-pot-rung"
            className={`absolute inset-y-0 z-20 w-0.5 ${
              rung.reached ? 'bg-primary-foreground/70' : 'bg-foreground/40'
            }`}
            style={{ left: `${rung.percent}%` }}
          />
        ))}
      </div>

      {/*
       * THE RULER, DIRECTLY UNDER THE TRACK, because a scale belongs against the thing it
       * measures. Numbers only: the ruler has no ticks of its own, so the bust line is the one
       * mark on this bar and the number beneath it is that line's caption for free.
       *
       * The two ends are pulled inside the track rather than centred on their own point — a "0%"
       * hanging half off the left edge reads as a layout accident where a rung's multiplier
       * hanging off the same edge reads as a caption for a mark that is plainly there. Everything
       * between them is centred.
       *
       * Drawn at every battery setting and in both mount sites, unchanged: there is nothing here
       * that moves, so there is nothing for `still` to settle.
       */}
      <div
        aria-hidden
        data-testid="duel-pot-scale"
        className="relative mt-0.5 h-2.5 text-[9px] font-medium tabular-nums text-muted-foreground"
      >
        {scale.map((percent, index) => (
          <span
            key={percent}
            data-testid="duel-pot-scale-tick"
            data-percent={percent}
            className={`absolute leading-none ${scaleAlign(index, scale.length)}`}
            style={{ left: `${percent}%` }}
          >
            {percent}%
          </span>
        ))}
      </div>

      {/*
       * The three regions named, under the bar they are drawn on.
       *
       * It is a row of its own rather than a legend, because the whole point is that the caption
       * is *the same width as the band it names* — the bounds are read off where the words stop,
       * which is the one thing a legend cannot say. It is drawn whatever the climb has reached
       * and whatever the battery setting is: this is the picture of the game, not a flourish.
       */}
      <div
        aria-hidden
        data-testid="duel-pot-bands"
        className="relative mt-1 h-3 text-[9px] font-semibold uppercase tracking-wide"
      >
        {pot.penaltyBand === null ? null : (
          <BandCaption band={pot.penaltyBand} testId="duel-pot-band-early-caption">
            too early
          </BandCaption>
        )}
        <BandCaption band={pot.bustBand} testId="duel-pot-band-bust-caption">
          can burst
        </BandCaption>
      </div>

      <p className="mt-1 flex items-baseline justify-between gap-2 text-[11px] text-muted-foreground">
        <span data-testid="duel-pot-next">
          {pot.nextRung === null
            ? 'Top rung — it is only worth more by lasting.'
            : `Next rung ${pot.nextRung.percent}% · ${formatPotMultiplier(pot.nextRung.multiplier)}`}
        </span>
        {pot.inDanger ? (
          <span className="shrink-0 font-semibold text-destructive">Can burst</span>
        ) : (
          <span className="shrink-0">Safe to {pot.dangerFromPercent}%</span>
        )}
      </p>
    </div>
  );
}

/**
 * The track's own width in CSS pixels, or 0 while nothing has been laid out.
 *
 * A layout effect rather than an effect, and a window listener rather than a `ResizeObserver`:
 * `sayings-picker.tsx`' `useAnchoredAbove` exactly, for its stated reasons. The track is a block
 * in the flow of a card whose width is its container's, so the only things that change it are a
 * resize and an orientation change — where an observer would fire on every re-render of a panel
 * that also holds a climbing percentage, ten times a second, for a rectangle that has not moved.
 *
 * **Zero is the honest answer for "not measured", and `potScaleTicks` answers the coarse scale to
 * it** — `anchor.ts`' `isMeasured` rule: the frame before a measurement exists is on screen, and
 * a rectangle of zeroes must produce the modest fallback rather than the maximal one. jsdom
 * measures everything as zero and so gets the same three numbers.
 */
function useTrackWidth(ref: RefObject<HTMLElement | null>): number {
  const [width, setWidth] = useState(0);

  useLayoutEffect(() => {
    const measure = () => setWidth(ref.current?.getBoundingClientRect().width ?? 0);
    measure();
    window.addEventListener('resize', measure);
    window.addEventListener('orientationchange', measure);
    return () => {
      window.removeEventListener('resize', measure);
      window.removeEventListener('orientationchange', measure);
    };
  }, [ref]);

  return width;
}

/** Where a scale label sits relative to its own point: the ends are pulled inside the track. */
function scaleAlign(index: number, count: number): string {
  if (index === 0) return '';
  return index === count - 1 ? '-translate-x-full' : '-translate-x-1/2';
}

/** One band's shading, positioned and sized from the declared range and nothing else. */
function BandShade({
  band,
  className,
  testId,
}: {
  band: DuelPotBand;
  className: string;
  testId: string;
}) {
  return (
    <span
      aria-hidden
      data-testid={testId}
      className={`absolute inset-y-0 z-0 ${className}`}
      style={bandBox(band)}
    />
  );
}

/** The word under a band, laid out over exactly the stretch it names. */
function BandCaption({
  band,
  testId,
  children,
}: {
  band: DuelPotBand;
  testId: string;
  children: string;
}) {
  return (
    <span
      aria-hidden
      data-testid={testId}
      className="absolute inset-y-0 overflow-hidden text-center leading-none text-muted-foreground"
      style={bandBox(band)}
    >
      {children}
    </span>
  );
}

/** A declared range as a box on the track. Percentages, so the bar scales with its container. */
function bandBox(band: DuelPotBand): { left: string; width: string } {
  return {
    left: `${band.fromPercent}%`,
    width: `${Math.max(0, band.toPercent - band.fromPercent)}%`,
  };
}
